// API Configuration for Arny Sidecar
export const API_CONFIG = {
  // Use internal VPC endpoint in production for better performance
  BASE_URL: process.env.REACT_APP_API_INTERNAL_URL || process.env.REACT_APP_API_BASE_URL || 'https://api.arny.ai',
  WEBSOCKET_URL: process.env.REACT_APP_WEBSOCKET_URL || 'wss://api.arny.ai/ws',
  // External API URL for fallback (when internal VPC is not available)
  EXTERNAL_BASE_URL: process.env.REACT_APP_API_BASE_URL || 'https://api.arny.ai',
  ENDPOINTS: {
    // Authentication
    LOGIN: '/auth/login',
    CALLBACK: '/auth/callback',
    ME: '/auth/me',
    
    // Chat Management
    CREATE_CHAT: (userId: string) => `/api/chat/users/${userId}/chats`,
    GET_USER_CHATS: (userId: string) => `/api/chat/users/${userId}/chats`,
    GET_CHAT: (chatId: string) => `/api/chat/chats/${chatId}`,
    UPDATE_CHAT: (chatId: string) => `/api/chat/chats/${chatId}`,
    DELETE_CHAT: (chatId: string) => `/api/chat/chats/${chatId}`,
    GET_CHAT_MESSAGES: (chatId: string) => `/api/chat/chats/${chatId}/messages`,
    ADD_MESSAGE: (chatId: string) => `/api/chat/chats/${chatId}/messages`,
    
    // Documentation
    WEBSOCKET_DOCS: '/api/docs/websocket',
    API_OVERVIEW: '/api/docs/overview',
    HEALTH_CHECK: '/api/docs/health',
  }
};

// Helper function to get the appropriate API base URL
export const getApiBaseUrl = (): string => {
  // Check if we're in a VPC environment (internal network)
  const isVpcEnvironment = process.env.REACT_APP_VPC_ENVIRONMENT === 'true';
  const internalUrl = process.env.REACT_APP_API_INTERNAL_URL;
  
  // Check if current page is served over HTTPS
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  
  // If we're on HTTPS but internal endpoint is HTTP, fall back to external API to avoid Mixed Content error
  if (isVpcEnvironment && internalUrl && !isHttps) {
    console.log('Using internal VPC endpoint for API calls:', internalUrl);
    return internalUrl;
  }
  
  if (isHttps && internalUrl && internalUrl.startsWith('http:')) {
    console.log('Mixed Content detected: HTTPS page with HTTP internal API. Falling back to external HTTPS API:', API_CONFIG.EXTERNAL_BASE_URL);
    return API_CONFIG.EXTERNAL_BASE_URL;
  }
  
  if (isVpcEnvironment && internalUrl) {
    console.log('Using internal VPC endpoint for API calls:', internalUrl);
    return internalUrl;
  }
  
  return API_CONFIG.EXTERNAL_BASE_URL;
};

// Enhanced API configuration with environment detection
export const getApiConfig = () => {
  return {
    ...API_CONFIG,
    BASE_URL: getApiBaseUrl(),
  };
};

// API Response Types
export interface ChatResponse {
  id: number;
  chat_id: string;
  user_id: number;
  title: string | null;
  is_active: boolean;
  chat_metadata: Record<string, any> | null;
  created_at: string;
  updated_at: string;
}

export interface ChatCreate {
  chat_id: string;
  user_id: string;
  title?: string;
  chat_metadata?: Record<string, any>;
}

export interface ChatListResponse {
  chats: ChatResponse[];
  total: number;
}

export interface MessageResponse {
  id: number;
  chat_id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  message_metadata: Record<string, any> | null;
  created_at: string;
}

export interface MessageCreate {
  chat_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  message_metadata?: Record<string, any>;
}

export interface MessageListResponse {
  messages: MessageResponse[];
  total: number;
}

export interface ChatUpdateRequest {
  title: string;
}

// WebSocket Message Types
export interface WebSocketMessage {
  query: string;
  chat_id?: string;
  plan_mode?: boolean;
  chat_title?: string;
}

export interface WebSocketResponse {
  status: 'streaming' | 'completed' | 'failed';
  result: string;
  agent?: string;
  final: boolean;
} 